// Claude Post Generator Service
import { PostGenerationRequest, GeneratedPost } from '../types/social.types';

export class ClaudePostGenerator {
  private apiKey: string;
  // Używamy backend proxy zamiast bezpośredniego API
  private apiUrl = process.env.NODE_ENV === 'production' 
    ? 'https://api.plexisystem.pl/api/universal-ai/chat'
    : 'http://localhost:3001/api/universal-ai/chat';

  constructor(apiKey: string) {
    this.apiKey = apiKey;
  }

  async generatePost(request: PostGenerationRequest): Promise<GeneratedPost> {
    const prompt = this.buildPrompt(request);
    
    try {
      // Wywołanie przez backend proxy
      const response = await fetch(this.apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token') || ''}` // Dodaj token jeśli jest
        },
        body: JSON.stringify({
          module_name: 'social_media',
          messages: [{
            role: 'user',
            content: prompt
          }],
          options: {
            max_tokens: 1000,
            temperature: 0.8
          }
        })
      });

      if (!response.ok) {
        throw new Error('Failed to generate post');
      }

      const data = await response.json();
      // Universal AI Service zwraca content bezpośrednio
      const responseText = data.content || data.response || '';
      return this.parseResponse(responseText, request);
    } catch (error) {
      console.error('Error generating post:', error);
      throw error;
    }
  }

  private buildPrompt(request: PostGenerationRequest): string {
    return `Jesteś ekspertem od social media dla firmy PlexiSystem zajmującej się produkcją elementów z pleksi.

Stwórz post na temat: "${request.topic}"

Wymagania:
- Ton: ${request.tone === 'professional' ? 'profesjonalny' : 
         request.tone === 'casual' ? 'swobodny' : 
         request.tone === 'enthusiastic' ? 'entuzjastyczny' : 'informacyjny'}
- Długość: ${request.length === 'short' ? 'krótki (do 280 znaków)' : 
            request.length === 'medium' ? 'średni (280-500 znaków)' : 'długi (500-1000 znaków)'}
- Platformy: ${request.platforms.join(', ')}
${request.includeHashtags ? '- Dodaj odpowiednie hashtagi (5-10)' : ''}
${request.includeEmojis ? '- Użyj emoji dla zwiększenia zaangażowania' : ''}
${request.productMention ? `- Wspomnij o produkcie: ${request.productMention}` : ''}
${request.callToAction ? `- Zakończ wezwaniem do działania: ${request.callToAction}` : ''}

Zwróć odpowiedź w formacie JSON:
{
  "content": "główna treść posta",
  "hashtags": ["hashtag1", "hashtag2"],
  "estimatedEngagement": liczba 1-10,
  "suggestedImages": ["opis_obrazu_1", "opis_obrazu_2"],
  "variants": [
    {
      "platform": "facebook",
      "content": "wersja dla Facebooka"
    },
    {
      "platform": "linkedin",
      "content": "wersja dla LinkedIn"
    }
  ]
}`;
  }

  private parseResponse(response: string, request: PostGenerationRequest): GeneratedPost {
    try {
      // Próbuj sparsować JSON z odpowiedzi
      const jsonMatch = response.match(/\{[\s\S]*\}/);
      if (jsonMatch) {
        const parsed = JSON.parse(jsonMatch[0]);
        return {
          content: parsed.content || '',
          hashtags: parsed.hashtags || [],
          estimatedEngagement: parsed.estimatedEngagement || 5,
          suggestedImages: parsed.suggestedImages || [],
          variants: parsed.variants || []
        };
      }
    } catch (error) {
      console.error('Error parsing AI response:', error);
    }

    // Fallback - zwróć podstawową strukturę
    return {
      content: response,
      hashtags: this.extractHashtags(response),
      estimatedEngagement: 5,
      suggestedImages: ['Zdjęcie produktu z pleksi', 'Proces produkcji'],
      variants: request.platforms.map(platform => ({
        platform,
        content: this.adaptContentForPlatform(response, platform)
      }))
    };
  }

  private extractHashtags(content: string): string[] {
    const hashtagRegex = /#\w+/g;
    const matches = content.match(hashtagRegex);
    return matches ? matches.map(tag => tag.substring(1)) : [];
  }

  private adaptContentForPlatform(content: string, platform: string): string {
    switch (platform) {
      case 'twitter':
        // Skróć do 280 znaków
        return content.length > 280 ? content.substring(0, 277) + '...' : content;
      case 'linkedin':
        // Dodaj profesjonalny ton
        return `🏢 ${content}\n\n#PlexiSystem #Produkcja #Innowacja`;
      case 'facebook':
        // Dodaj więcej emoji i CTA
        return `${content}\n\n👉 Sprawdź naszą ofertę: www.plexisystem.pl`;
      case 'instagram':
        // Dodaj więcej hashtagów na końcu
        return `${content}\n.\n.\n.\n#plexi #pleksi #produkcja #reklama #design`;
      default:
        return content;
    }
  }

  async generateContentIdeas(topic: string, count: number = 5): Promise<string[]> {
    const prompt = `Zaproponuj ${count} pomysłów na posty w social media dla firmy PlexiSystem (produkcja z pleksi) na temat: "${topic}". 
    Zwróć listę krótkich tematów (max 50 znaków każdy) w formacie JSON: {"ideas": ["temat1", "temat2", ...]}`;

    try {
      // Wywołanie przez backend proxy
      const response = await fetch(this.apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token') || ''}`
        },
        body: JSON.stringify({
          module_name: 'social_media',
          messages: [{
            role: 'user',
            content: prompt
          }],
          options: {
            max_tokens: 500,
            temperature: 0.9
          }
        })
      });

      const data = await response.json();
      const content = data.content || data.response || '';
      const jsonMatch = content.match(/\{[\s\S]*\}/);
      
      if (jsonMatch) {
        const parsed = JSON.parse(jsonMatch[0]);
        return parsed.ideas || [];
      }
      
      return [];
    } catch (error) {
      console.error('Error generating content ideas:', error);
      return [];
    }
  }
}

// Przykładowe szablony postów
export const postTemplates = {
  productShowcase: {
    title: 'Prezentacja produktu',
    template: '🎯 Nowość w ofercie! {product_name}\n\n✨ {features}\n\n💡 Idealne do: {use_cases}\n\n📞 Zamów już dziś!',
    variables: ['product_name', 'features', 'use_cases']
  },
  
  behindTheScenes: {
    title: 'Kulisy produkcji',
    template: '🏭 Zajrzyj za kulisy PlexiSystem!\n\n{description}\n\n🔧 {process_detail}\n\n#produkcja #plexi #jakość',
    variables: ['description', 'process_detail']
  },
  
  customerStory: {
    title: 'Historia klienta',
    template: '🌟 Success story!\n\n{client_name} potrzebował {need}.\n\n✅ Nasze rozwiązanie: {solution}\n\n💬 "{testimonial}"',
    variables: ['client_name', 'need', 'solution', 'testimonial']
  },
  
  educationalPost: {
    title: 'Post edukacyjny',
    template: '📚 Czy wiesz, że...?\n\n{fact}\n\n💡 {explanation}\n\n🔗 Dowiedz się więcej na naszym blogu!',
    variables: ['fact', 'explanation']
  },
  
  seasonalPromotion: {
    title: 'Promocja sezonowa',
    template: '🎉 {season} w PlexiSystem!\n\n🏷️ {offer}\n\n⏰ Oferta ważna do: {end_date}\n\n📞 Nie zwlekaj!',
    variables: ['season', 'offer', 'end_date']
  }
};
